import * as Sentry from '@sentry/react';
import { Auth } from 'aws-amplify';
import localforage from 'localforage';
import { v4 as uuid4 } from 'uuid';

import { selectedBorerIdKey } from '@/mobx-models/Equipment/selectedBorerIdKey';
import { TrackingEventType } from '@/rxdb/replication-v2/TrackingEvent';

import { createTrackingEvent } from '../../graphql/TrackingEvent';
import { rootStore } from '../../mobx-models/Root';
import { useOnlineStatus } from '..';
import useDebug from './useDebug';

// DEPRECATED: Use directly from `src/rxdb/replication-v2/TrackingEvent.ts`
export { TrackingEventType };

export const NO_EMAIL_FOUND = 'NO_EMAIL_FOUND';

export const trackingEventStore = localforage.createInstance({
  name: 'tracking-event-store',
});

export const getUserEmailForTracking = (defaultEmail: string) => {
  let userEmail = defaultEmail;
  if (defaultEmail === NO_EMAIL_FOUND) {
    Sentry.withScope(scope => {
      userEmail = scope.getUser()?.email ?? NO_EMAIL_FOUND;
    });
  }
  return userEmail;
};

const isJestOrStorybook = () => {
  return (
    process.env.JEST_WORKER_ID !== undefined ||
    process.env.STORYBOOK_MODE !== undefined ||
    process.env.VITEST_POOL_ID !== undefined
  );
};

// get the borers name and id from storage
const getBorerDetails = () => {
  const data = localStorage.getItem(selectedBorerIdKey);

  const borerDetails = {
    id: '',
    shortName: '',
  };

  if (data) {
    const parsedData = JSON.parse(data);
    borerDetails.id = parsedData.id;
    borerDetails.shortName = parsedData.shortName;
  }

  return borerDetails;
};

// get user details
const getUserDetails = async () => {
  let userInfo = undefined;
  const userDetails = {
    email: '',
  };
  try {
    userInfo = await Auth.currentUserInfo();
    if (userInfo) {
      userDetails.email = userInfo?.attributes?.email || NO_EMAIL_FOUND;
    }
  } catch (err) {
    console.error('🚀 ~ file: useEventTracking.ts ~ getUserDetails: line 40 ~ err', err);
  }

  return userDetails;
};

// Sends An Event to the server
const dispatchEvent = async (
  type: string,
  blob: string,
  eventTimeOnDevice: string,
  siteId = rootStore.user.siteId,
) =>
  createTrackingEvent({
    siteId,
    eventType: type,
    eventTimeOnDevice,
    eventData: blob,
  });

// Cache Event
const cacheEvent = async (
  id: string,
  type: string,
  blob: string,
  eventTimeOnDevice: string,
  siteId: string,
) =>
  trackingEventStore.setItem(id, {
    type,
    blob,
    eventTimeOnDevice,
    siteId,
  });

// Track Event
export const trackEventFn = async (
  type: string,
  blob: Record<string, any>,
  isOnline = true,
  DEBUG = false,
) => {
  // Create event data
  const eventTimeOnDevice = new Date().toISOString();
  const borerDetails = getBorerDetails();
  const userDetails = await getUserDetails();

  const extraBlobData = {
    ...blob,
    shortName: borerDetails.shortName,
    borerId: borerDetails.id,
    email: getUserEmailForTracking(userDetails.email),
    id: uuid4(),
    unixTimestamp: Date.now(),
  };
  const blobString = JSON.stringify(extraBlobData);

  // If online dispatch to server
  if (isOnline) {
    if (DEBUG) {
      console.log('[useEventTracking] Dispatching event to server');
      console.dir([type, blobString, eventTimeOnDevice]);
    }
    try {
      const result = await dispatchEvent(type, blobString, eventTimeOnDevice);
      if (DEBUG) {
        console.log('[useEventTracking] Event dispatched to server');
        console.dir(result);
      }
    } catch (error) {
      if (DEBUG) {
        console.log('🚀 ~ file: useEventTracking.ts ~ line 27 ~ trackEvent ~ error', error);
        console.log('[useEventTracking] Error sending event storing event locally');
      }
      await cacheEvent(
        extraBlobData.id,
        type,
        blobString,
        eventTimeOnDevice,
        rootStore.user.siteId,
      );
    }
  }
  // else store in local storage
  else {
    if (DEBUG) console.log('[useEventTracking] Storing event locally');
    await cacheEvent(extraBlobData.id, type, blobString, eventTimeOnDevice, rootStore.user.siteId);
  }
};

export const useEventTracking = () => {
  const isOnline = useOnlineStatus();
  const DEBUG = useDebug();

  // Send cached events
  const sendCachedEvents = async () => {
    if (DEBUG) console.log('[useEventTracking] Sending cached events');
    const cachedEvents = await trackingEventStore.keys();
    if (DEBUG) {
      console.log('[useEventTracking] Cached events');
      console.dir(cachedEvents);
    }
    cachedEvents.forEach(async (key: string) => {
      try {
        const { type, blob, eventTimeOnDevice, siteId } = await trackingEventStore.getItem(key);
        if (DEBUG)
          console.log('[useEventTracking] Event data', type, blob, eventTimeOnDevice, siteId);
        await dispatchEvent(type, blob, eventTimeOnDevice, siteId);
        await trackingEventStore.removeItem(key);
      } catch (error) {
        if (DEBUG) {
          console.log('🚀 ~ file: useEventTracking.ts ~ line 67 ~ sendCachedEvents ~ error', error);
          console.log('[useEventTracking] Error sending cached event', key);
        }
      }
    });

    if (DEBUG) console.log('[useEventTracking] Cached events sent');
  };

  const trackEvent = (type: TrackingEventType, blob: Record<string, any>) => {
    if (isJestOrStorybook()) return;
    return trackEventFn(type, blob, isOnline, DEBUG);
  };

  return { trackEvent, sendCachedEvents };
};

export default useEventTracking;
